import { useEffect, useState } from "react";
import { http, currency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash, Lightning, Broadcast, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = {
  name: "", price: 799, quota_gb: "", description: "",
  rate_limit_up_kbps: "", rate_limit_down_kbps: "", address_pool: "",
  ip_pool_id: "", ip_pool_id_v6: "",
  service_type: "Framed-User",
  validity_days: "",
  validity_unit: "days",
  nas_ids: [],
  rx_low_dbm: "", rx_high_dbm: "",
};

// Convert stored `validity_days` back to the most human-friendly unit so
// editing a "1 month" plan doesn't show up as "30 days" and back.
const daysToUnit = (d) => {
  const n = Number(d);
  if (!n || n <= 0) return { count: "", unit: "days" };
  if (n % 365 === 0) return { count: n / 365, unit: "years" };
  if (n % 30 === 0)  return { count: n / 30,  unit: "months" };
  return { count: n, unit: "days" };
};
const unitToDays = (count, unit) => {
  const n = Number(count);
  if (!n || n <= 0) return null;
  if (unit === "years")  return n * 365;
  if (unit === "months") return n * 30;
  return n;
};

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [pools, setPools] = useState([]);
  const [nasList, setNasList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [showAttrs, setShowAttrs] = useState({});

  const load = async () => setPlans((await http.get("/plans")).data);
  const loadPools = async () => {
    try { setPools((await http.get("/ip-pools")).data || []); } catch { /* noop */ }
  };
  const loadNas = async () => {
    try { setNasList((await http.get("/nas")).data || []); } catch { /* noop */ }
  };
  useEffect(() => { load(); loadPools(); loadNas(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    const t = daysToUnit(p.validity_days);
    setForm({
      name: p.name, price: p.price, quota_gb: p.quota_gb ?? "",
      description: p.description || "",
      rate_limit_up_kbps: p.rate_limit_up_kbps ?? "",
      rate_limit_down_kbps: p.rate_limit_down_kbps ?? "",
      address_pool: p.address_pool || "",
      ip_pool_id: p.ip_pool_id || "",
      ip_pool_id_v6: p.ip_pool_id_v6 || "",
      service_type: p.service_type || "Framed-User",
      validity_days: t.count,
      validity_unit: t.unit,
      nas_ids: Array.isArray(p.nas_ids) ? p.nas_ids : [],
      rx_low_dbm: p.rx_low_dbm ?? "",
      rx_high_dbm: p.rx_high_dbm ?? "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const cast = (v) => (v === "" || v === null ? null : Number(v));
    const body = {
      name: form.name,
      price: Number(form.price),
      quota_gb: cast(form.quota_gb),
      description: form.description,
      rate_limit_up_kbps: cast(form.rate_limit_up_kbps),
      rate_limit_down_kbps: cast(form.rate_limit_down_kbps),
      address_pool: form.address_pool || null,
      ip_pool_id: form.ip_pool_id || null,
      ip_pool_id_v6: form.ip_pool_id_v6 || null,
      service_type: form.service_type,
      validity_days: unitToDays(form.validity_days, form.validity_unit),
      nas_ids: form.nas_ids,
      rx_low_dbm: cast(form.rx_low_dbm),
      rx_high_dbm: cast(form.rx_high_dbm),
    };
    if (editing) await http.patch(`/plans/${editing}`, body);
    else await http.post("/plans", body);
    toast.success(editing ? "Plan updated" : "Plan created");
    setOpen(false);
    load();
  };

  const remove = async (id) => {
    if (!confirm("Delete plan?")) return;
    await http.delete(`/plans/${id}`); load();
  };

  const toggleAttrs = async (id) => {
    if (showAttrs[id]) { setShowAttrs({ ...showAttrs, [id]: null }); return; }
    const { data } = await http.get(`/plans/${id}/radius-attributes`);
    setShowAttrs({ ...showAttrs, [id]: data.attributes });
  };

  return (
    <div className="space-y-6 fade-up" data-testid="plans-view">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="uppercase-label">Service catalogue</div>
          <h1 className="font-display font-bold text-4xl mt-1">Internet plans</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Every plan carries the RADIUS attributes (rate-limit, framed-pool, session-timeout) that get pushed to the NAS at auth time.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-plan-btn" onClick={openNew} className="rounded-sm bg-primary hover:bg-primary/90"><Plus size={14} weight="bold" className="mr-2" /> New plan</Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-2xl">
            <DialogHeader><DialogTitle className="font-display">{editing ? "Edit plan" : "Create plan"}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="grid grid-cols-2 gap-4">
              <F label="Name" span={2}><Input required data-testid="plan-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></F>
              <F label="Price (INR)"><Input required type="number" data-testid="plan-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></F>
              <F label="Quota (GB, blank = unlimited)"><Input type="number" value={form.quota_gb} onChange={(e) => setForm({ ...form, quota_gb: e.target.value })} /></F>
              <F label="IPv4 pool (managed)"><select data-testid="plan-ippool-v4" value={form.ip_pool_id} onChange={(e) => setForm({ ...form, ip_pool_id: e.target.value })} className="w-full h-9 bg-secondary border border-border px-3 text-sm rounded-sm">
                <option value="">— none (use free-text below) —</option>
                {pools.filter((p) => p.family === "v4").map((p) => (
                  <option key={p.id} value={p.id}>{p.name} · {p.range_start}→{p.range_end}</option>
                ))}
              </select></F>
              <F label="IPv6 pool (optional)"><select data-testid="plan-ippool-v6" value={form.ip_pool_id_v6} onChange={(e) => setForm({ ...form, ip_pool_id_v6: e.target.value })} className="w-full h-9 bg-secondary border border-border px-3 text-sm rounded-sm">
                <option value="">— none —</option>
                {pools.filter((p) => p.family === "v6").map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select></F>
              <F label="Address pool free-text (fallback)" span={2}><Input value={form.address_pool} onChange={(e) => setForm({ ...form, address_pool: e.target.value })} placeholder="pool-home — MUST match a pool name defined on the MikroTik/BNG (leave blank to let the router pick from its default profile pool)" /></F>

              <F label="Plan tenure">
                <div className="flex gap-2">
                  <Input type="number" min="0" data-testid="plan-validity" value={form.validity_days}
                         onChange={(e) => setForm({ ...form, validity_days: e.target.value })}
                         placeholder="1 = monthly, 3 = quarterly" className="flex-1" />
                  <select data-testid="plan-validity-unit" value={form.validity_unit}
                          onChange={(e) => setForm({ ...form, validity_unit: e.target.value })}
                          className="h-9 bg-secondary border border-border px-2 text-sm rounded-sm">
                    <option value="days">Days</option>
                    <option value="months">Months</option>
                    <option value="years">Years</option>
                  </select>
                </div>
              </F>
              <F label="Applies to NAS (leave empty = all)">
                <div className="mt-1 flex flex-wrap gap-1 max-h-24 overflow-y-auto border border-border p-2 rounded-sm bg-background" data-testid="plan-nas-picker">
                  {nasList.length === 0 && <span className="text-xs text-muted-foreground italic">No NAS defined yet</span>}
                  {nasList.map((n) => {
                    const on = form.nas_ids.includes(n.id);
                    return (
                      <label key={n.id} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm cursor-pointer border ${on ? "bg-primary text-primary-foreground border-primary" : "bg-secondary border-border"}`}>
                        <input type="checkbox" className="hidden" checked={on} onChange={() => setForm((f) => ({ ...f, nas_ids: on ? f.nas_ids.filter((x) => x !== n.id) : [...f.nas_ids, n.id] }))} />
                        {n.name}
                      </label>
                    );
                  })}
                </div>
              </F>

              <div className="col-span-2 pt-2 border-t border-border">
                <div className="uppercase-label">PPPoE / RADIUS attributes</div>
              </div>
              <F label="Rate-limit ↑ (Kbps)"><Input type="number" value={form.rate_limit_up_kbps} onChange={(e) => setForm({ ...form, rate_limit_up_kbps: e.target.value })} placeholder="50000" data-testid="plan-up" /></F>
              <F label="Rate-limit ↓ (Kbps)"><Input type="number" value={form.rate_limit_down_kbps} onChange={(e) => setForm({ ...form, rate_limit_down_kbps: e.target.value })} placeholder="100000" data-testid="plan-down" /></F>
              <div className="col-span-2 pt-2 border-t border-border">
                <div className="uppercase-label">Fibre health alerts</div>
                <p className="text-[11px] text-muted-foreground mt-1">Optional. If an ONU's RX-power falls outside this window during auto-link, a yellow alert lands on the OLT Dashboard so field engineers can act before the customer calls.</p>
              </div>
              <F label="RX low threshold (dBm)"><Input type="number" step="0.1" data-testid="plan-rx-low" value={form.rx_low_dbm} onChange={(e) => setForm({ ...form, rx_low_dbm: e.target.value })} placeholder="-28" /></F>
              <F label="RX high threshold (dBm)"><Input type="number" step="0.1" data-testid="plan-rx-high" value={form.rx_high_dbm} onChange={(e) => setForm({ ...form, rx_high_dbm: e.target.value })} placeholder="-8" /></F>
              <F label="Description" span={2}><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></F>
              <DialogFooter className="col-span-2"><Button type="submit" data-testid="plan-submit" className="rounded-sm bg-primary hover:bg-primary/90">{editing ? "Update" : "Create"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {plans.map((p) => (
          <div key={p.id} className="border border-border bg-card p-5 relative group hover:border-primary/60 transition-colors" data-testid={`plan-card-${p.id}`}>
            <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition">
              <button onClick={() => openEdit(p)} className="text-muted-foreground hover:text-primary" title="Edit" data-testid={`plan-edit-${p.id}`}><PencilSimple size={14} /></button>
              <button onClick={() => remove(p.id)} className="text-muted-foreground hover:text-destructive" title="Delete"><Trash size={14} /></button>
            </div>
            <div className="flex items-center gap-2">
              <Lightning size={16} weight="bold" className="text-primary" />
              <div className="uppercase-label">Fiber plan</div>
            </div>
            <h3 className="font-display text-2xl font-bold mt-2">{p.name}</h3>
            <div className="mt-4 flex items-end gap-2">
              <span className="font-display font-bold text-4xl">{currency(p.price)}</span>
              <span className="text-xs text-muted-foreground pb-1">/ month</span>
            </div>
            <div className="mt-4 border-t border-border pt-4 space-y-2 text-sm">
              <Row k="Speed" v={<span className="font-mono">{p.speed_mbps} Mbps</span>} />
              <Row k="Quota" v={<span className="font-mono">{p.quota_gb ? `${p.quota_gb} GB` : "Unlimited"}</span>} />
              <Row k="Tenure" v={<span className="font-mono">{(() => {
                const t = daysToUnit(p.validity_days);
                return t.count ? `${t.count} ${t.unit}` : "—";
              })()}</span>} />
              <Row k="Rate limit" v={<span className="font-mono">{(p.rate_limit_up_kbps || "-") + "/" + (p.rate_limit_down_kbps || "-") + "k"}</span>} />
              <Row k="Pool" v={<span className="font-mono">{pools.find((x) => x.id === p.ip_pool_id)?.name || p.address_pool || "—"}</span>} />
              {(p.rx_low_dbm != null || p.rx_high_dbm != null) && (
                <Row k="RX window" v={<span className="font-mono text-[hsl(var(--warning))]">{p.rx_low_dbm ?? "—"} … {p.rx_high_dbm ?? "—"} dBm</span>} />
              )}
            </div>
            {p.description && <p className="text-xs text-muted-foreground mt-4">{p.description}</p>}

            <button
              onClick={() => toggleAttrs(p.id)}
              className="mt-4 text-xs text-primary hover:underline inline-flex items-center gap-1 font-mono"
              data-testid={`plan-attrs-${p.id}`}
            >
              <Broadcast size={12} /> {showAttrs[p.id] ? "hide" : "show"} RADIUS attributes
            </button>
            {showAttrs[p.id] && (
              <pre className="mt-3 bg-secondary border border-border p-3 text-[11px] font-mono overflow-x-auto whitespace-pre-wrap">
{Object.entries(showAttrs[p.id]).map(([k, v]) => `${k} = ${v}`).join("\n")}
              </pre>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const F = ({ label, span = 1, children }) => (
  <div className={span === 2 ? "col-span-2" : ""}>
    <Label className="uppercase-label">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
const Row = ({ k, v }) => (
  <div className="flex justify-between font-mono"><span className="text-muted-foreground">{k}</span><span>{v}</span></div>
);
